import { Alert, Button, Card, Form, Input, Space, Typography } from 'antd';
import { Component } from 'react';
import { cardmargin, grayheader } from '../../CSSReferences';

const { Paragraph } = Typography;

export interface PasswortwechselProps {
  /** true, solange das Konto noch mit einem ausgelieferten Passwort arbeitet. */
  pflicht?: boolean;
  onSubmit?: (altesPasswort: string, neuesPasswort: string) => void;
}

/**
 * Seite zum Wechseln des eigenen Passworts.
 *
 * Sie wird nach der Anmeldung angesteuert, wenn der Server <c>pw_status</c> 1
 * meldet - das Konto arbeitet dann noch mit einem Passwort, das jemand anderes
 * vergeben hat (Auslieferung, Anlage durch einen Administrator, Zuruecksetzen
 * per Mail). Bis zum Wechsel weist die API jeden anderen Aufruf mit 403 ab;
 * diese Seite ist also kein Vorschlag, sondern der einzige offene Weg.
 *
 * Die Mindestlaenge steht auch im Server (Vorgabe 12 Zeichen). Die Pruefung
 * hier erspart einen Rundlauf, ersetzt ihn aber nicht.
 */
const MINDESTLAENGE = 12;

export default class PasswortwechselView extends Component<PasswortwechselProps, {}> {
  absenden = (werte: { alt: string; neu: string }) => {
    if (this.props.onSubmit) {
      this.props.onSubmit(werte.alt, werte.neu);
    }
  };

  render() {
    return (
      <Card
        title="Passwort ändern"
        style={cardmargin}
        styles={{ header: grayheader }}
      >
        <Space orientation="vertical" size={16} style={{ width: '100%' }}>
          {this.props.pflicht && (
            <Alert
              type="warning"
              showIcon
              message="Passwortwechsel erforderlich"
              description={
                <Paragraph style={{ marginBottom: 0 }}>
                  Dieses Konto arbeitet noch mit dem ausgelieferten Passwort.
                  Solange es gilt, sind alle anderen Funktionen gesperrt —
                  ein Passwort, das nicht Sie gewählt haben, kennen
                  möglicherweise auch andere.
                </Paragraph>
              }
            />
          )}

          <Form
            layout="vertical"
            onFinish={this.absenden}
            style={{ maxWidth: '32em' }}
          >
            <Form.Item
              label="Bisheriges Passwort"
              name="alt"
              rules={[{ required: true, message: 'Bitte das bisherige Passwort eingeben.' }]}
            >
              <Input.Password autoComplete="current-password" />
            </Form.Item>

            <Form.Item
              label="Neues Passwort"
              name="neu"
              rules={[
                { required: true, message: 'Bitte ein neues Passwort eingeben.' },
                {
                  min: MINDESTLAENGE,
                  message: `Mindestens ${MINDESTLAENGE} Zeichen.`,
                },
              ]}
            >
              <Input.Password autoComplete="new-password" />
            </Form.Item>

            <Form.Item
              label="Neues Passwort wiederholen"
              name="neu2"
              dependencies={['neu']}
              rules={[
                { required: true, message: 'Bitte das neue Passwort wiederholen.' },
                ({ getFieldValue }) => ({
                  validator(_rule: unknown, wert: string) {
                    if (!wert || getFieldValue('neu') === wert) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error('Die beiden Eingaben stimmen nicht überein.'),
                    );
                  },
                }),
              ]}
            >
              <Input.Password autoComplete="new-password" />
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit">
                Passwort ändern
              </Button>
            </Form.Item>
          </Form>

          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            Länge schlägt Komplexität: Eine Folge mehrerer Wörter ist sicherer
            und leichter zu merken als ein kurzes Passwort mit Sonderzeichen.
            Zeichenklassen schreiben wir deshalb nicht vor.
          </Paragraph>
        </Space>
      </Card>
    );
  }
}
